import React, { useMemo } from 'react';
import { styled, useStyleConfig, vui } from '../core';
import { cs, filterUndefined, isReactText, isString } from '../utils';
import { TagProvider } from './context';
import TagButton from './tagButton';
import TagIcon from './tagIcon';
import TagText from './tagText';
export const TagBase = styled.spanBox `
  align-items: center;
  border-radius: xl;
  display: inline-flex;
  font-weight: medium;
  justify-content: center;
  line-height: normal;
  max-width: 100%;
  position: relative;
  transition-duration: fast;
  width: fit-content;

  &:focus {
    border-color: transparent;
    outline: none;
  }

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    user-select: none;
  }
`;
/**
 * Displays text, icons or custom content. Can be made interactive or a link.
 * Exposes some props to the children via context.
 */
export const Tag = vui((props, ref) => {
    const { casing, children, className, disabled, icon, iconLeft, iconRight, isFullWidth, isInteractive = props.onClick !== undefined && props.onDelete === undefined, isRound, isTruncated = true, itemLeft, itemRight, onClick, onDelete, size = 'md', text, variant = 'subtleBlue', weight, ...rest } = props;
    const styles = useStyleConfig('Tag', props);
    const { activeBg, h, hoverBg, spaceX: spaceXBase = 0, ...tagStyles } = styles.container;
    const context = useMemo(() => filterUndefined({ disabled, size, variant }), [disabled, size, variant]);
    const spaceX = spaceXBase / 8;
    let spaceXText = (spaceXBase + 4) / 8;
    if (size === 'xs') {
        spaceXText = spaceXBase / 8;
    }
    const pl = icon || itemLeft || iconLeft ? spaceX : spaceXText;
    const pr = icon || itemRight || iconRight ? spaceX : spaceXText;
    const interactiveProps = !disabled && isInteractive
        ? {
            cursor: 'pointer',
            hoverBg,
            activeBg,
            tabIndex: 0,
            userSelect: 'none'
        }
        : {};
    const aliasedProps = filterUndefined({
        borderRadius: isRound ? h / 2 : undefined,
        'aria-disabled': disabled,
        fontWeight: weight,
        textTransform: casing,
        w: isFullWidth ? '100%' : undefined
    });
    return (React.createElement(TagProvider, { value: context },
        React.createElement(TagBase, { className: cs('vui-tag', className), focusRing: 2, h: h, isTruncated: isTruncated, onClick: !disabled ? onClick : undefined, pl: pl, pr: pr, ref: ref, ...tagStyles, ...interactiveProps, ...aliasedProps, ...rest },
            isString(icon) ? React.createElement(TagIcon, { name: icon }) : icon,
            !icon && (React.createElement(React.Fragment, null,
                itemLeft,
                isString(iconLeft) ? React.createElement(TagIcon, { mr: spaceX, name: iconLeft }) : iconLeft,
                children ?? (isReactText(text) ? React.createElement(TagText, { ...{ isTruncated, text } }) : text),
                isString(iconRight) ? React.createElement(TagIcon, { ml: spaceX, name: iconRight }) : iconRight,
                onDelete ? React.createElement(TagButton, { disabled: disabled, isRound: isRound, ml: spaceX, onClick: onDelete }) : itemRight)))));
});
Tag.Button = TagButton;
Tag.Icon = TagIcon;
Tag.Text = TagText;
export default Tag;
